// Admin Web client for the browser-safe H-8 settlement-control BFF.
//
// Calls ONLY the session-gated BFF routes (/v2/admin/bff/settlement-control/*).
// Never the HMAC service-auth routes (/v2/admin/settlement-control/*), never an
// HMAC header — `apiCall` attaches only the admin session bearer. The browser
// holds no HMAC/admin secret.
//
// The resume EXECUTE path is `low_config` on the BE: it requires an APPROVED
// multisign proposalId (threshold 1). Following the established Admin Web
// pattern (see partnerApplications.ts / WithdrawForm), the operator creates +
// approves a low_config proposal via the Approvals flow and supplies the
// approved proposalId here. This module never creates proposals.

import { apiCall } from "./client";

export interface SettlementControlStatus {
  breaker: { tripped: boolean; reason: string | null; trippedAt: string | null };
  settlementEnabled: boolean;
  onchainSettlementEnabled: boolean;
}

export interface ResumeIntentCheck {
  intentId: string;
  domain: string;
  status: string;
  billingStatus: string | null;
  /** Already truncated by the BE — never a full hash. */
  txHash: string | null;
  safeToResume: boolean;
  blockers: string[];
}

export interface ResumeDryRunResult {
  ok: true;
  dryRun: true;
  breaker: { tripped: boolean; reason: string | null; trippedAt: string | null };
  affectedCount: number;
  safeCount: number;
  blockedCount: number;
  intents: ResumeIntentCheck[];
  nextAction: "confirm_resume" | "already_clear" | "blocked";
}

export interface ResumeExecuteIntentResult {
  intentId: string;
  domain: string;
  before: string;
  after: string;
  billingAfter: string | null;
  domainCreated: boolean;
  mappingCreated: boolean;
  resolverOk: boolean;
}

export interface ResumeExecuteResult {
  ok: true;
  breakerReset: boolean;
  settledCount: number;
  stillProcessingCount: number;
  failedCount: number;
  results: ResumeExecuteIntentResult[];
  nextAction?: string;
}

export type ResumeResult = ResumeDryRunResult | ResumeExecuteResult;

export function isDryRunResult(r: ResumeResult): r is ResumeDryRunResult {
  return (r as ResumeDryRunResult).dryRun === true;
}

export async function getSettlementControlStatus(): Promise<SettlementControlStatus> {
  return apiCall<SettlementControlStatus>("/v2/admin/bff/settlement-control/status");
}

export interface ResumeInput {
  dryRun: boolean;
  targetIntentId?: string;
  reason: string;
  /** Required when dryRun=false — approved low_config proposalId. */
  proposalId?: string;
}

export async function resumeSettlementControl(input: ResumeInput): Promise<ResumeResult> {
  const body: Record<string, unknown> = { dryRun: input.dryRun, reason: input.reason };
  if (input.targetIntentId) body["targetIntentId"] = input.targetIntentId;
  if (input.proposalId) body["proposalId"] = input.proposalId;
  return apiCall<ResumeResult>("/v2/admin/bff/settlement-control/resume", {
    method: "POST",
    body,
  });
}

export interface ResumeProposalResponse {
  proposalId: string;
  actionType: "low_config";
  action: "resume";
  targetIntentId: string | null;
  status: string;
}

// Create the action-bound low_config proposal that resume.execute requires.
// Browser-session only (no HMAC). The BE binds the canonical payload
// (domain/action/targetIntentId); execute re-verifies it. The returned
// proposal is in `proposed` state — the operator approves it via Approvals.
export async function createResumeProposal(input: {
  targetIntentId?: string;
  reason: string;
}): Promise<ResumeProposalResponse> {
  const body: Record<string, unknown> = { reason: input.reason };
  if (input.targetIntentId) body["targetIntentId"] = input.targetIntentId;
  return apiCall<ResumeProposalResponse>(
    "/v2/admin/bff/settlement-control/resume/proposal",
    { method: "POST", body },
  );
}

/** A pasted value must be a real proposal id — never a payment-intent id
 *  (`pi_…`) or empty. Returns an error string, or null when acceptable. */
export function validateResumeProposalId(raw: string): string | null {
  const v = raw.trim();
  if (v.length === 0) return "Enter an approved resume proposalId.";
  if (v.toLowerCase().startsWith("pi_")) {
    return "That looks like a payment-intent id, not a proposalId.";
  }
  return null;
}
